import { useState } from "react";
import { Button, Col, Container, ListGroup, Modal, Row } from "react-bootstrap";
import { TrashIcon } from "@simplex/sx-icons";
import ProjectPanelHeaderToolbar from "../ui-components/ProjectPanelHeaderToolbar";
import ProjectPanelListToolbarView from "../ui-components/ProjectPanelListToolbarView";
import { useRangeAndToggleSelection } from "../../hooks/useRangeAndToggleSelection";
import type { Project } from "../../ProjectExplorer";
import type { LeftPanelProps } from "./panelProps";

export default function LeftPanel(props: LeftPanelProps) {
  const {
    t,
    setSelectedFile,
    setProjectName,
    setSelectedProjectName,
    setShowProjectDeleteModal,
    setCopyOfCurrentSelectedFile,
    access,
    config,
    user,
    selectedProjectName,
    uploadProjectFile,
    parseUTC,
    dropHandlerForProjects,
    setIsProjectDragOver,
    setDragOverId,
    dragOverId,
    isProjectDragOver,
    setCheckedCount,
    setSelectedFileIds,
    setSfileStatus,
    selectedProjectIds,
    setSelectedProjectIds,
    deleteProjectandFiles,
    getUser,
  } = props;

  const allProjects = Object.values(user.projectdigest || {}) as Project[];
  const hasMultiProjectSelection = selectedProjectIds.length > 1;
  const projectsPendingMultiDelete = allProjects.filter((project) => selectedProjectIds.includes(project.id));

  const [showMultiProjectDeleteModal, setShowMultiProjectDeleteModal] = useState(false);

  const focusedProjectId = allProjects.find((project) => project.name === selectedProjectName)?.id ?? null;
  const {
    setSelectionAnchorId,
    applyRangeSelection: applyProjectRangeSelection,
    toggleSelection: toggleProjectSelection,
  } = useRangeAndToggleSelection<Project>({
    setSelectedIds: setSelectedProjectIds,
    focusedId: focusedProjectId,
  });

  const handleDeleteSelectedProjects = async () => {
    await Promise.all(projectsPendingMultiDelete.map((project) => deleteProjectandFiles(project.guid)));
    await getUser();

    if (projectsPendingMultiDelete.some((project) => project.name === selectedProjectName)) {
      setSelectedProjectName(null);
      setSelectedFile(-1);
      setCopyOfCurrentSelectedFile(null);
    }

    setSelectedProjectIds([]);
    setSelectionAnchorId(null);
    setShowMultiProjectDeleteModal(false);
  };

  return (
    <Col md={3} className="pt-2 pb-3 h-100">
      <div id="left-panel" className="d-flex flex-column h-100" style={{ overflow: "hidden" }}>
        <Modal
          className="sxmodal"
          show={showMultiProjectDeleteModal}
          onHide={() => setShowMultiProjectDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("delete_modal.confirm_delete_title") || "Confirm Deletion"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t("delete_modal.confirm_delete_message")}</p>
            <ul>
              {projectsPendingMultiDelete.map((project) => (
                <li key={project.id}>
                  <b>{project.name}</b>
                </li>
              ))}
            </ul>
            <p>{t("following_files_will_be_deleted_")}</p>
            <ul>
              {projectsPendingMultiDelete.flatMap((project) => project.files.map((f) => <li key={f.id}>{f.name}</li>))}
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              className="action-btn-transparent"
              onClick={() => setShowMultiProjectDeleteModal(false)}
            >
              {t("buttons.cancel") || "Cancel"}
            </Button>
            <Button variant="danger" className="action-btn" onClick={handleDeleteSelectedProjects}>
              {t("buttons.delete") || "Delete"}
            </Button>
          </Modal.Footer>
        </Modal>

        <Row className="mb-3">
          <Col>
            <ProjectPanelHeaderToolbar
              id="project-list-toolbar-first"
              headerTitle={t("projects")}
              setSelectedFile={setSelectedFile}
              setSelectedFileName={setProjectName}
              setSelectedProjectName={setSelectedProjectName}
              setCopyOfCurrentSelectedFile={setCopyOfCurrentSelectedFile}
              beamAccess={access["beam-app"] == 200}
              foundationAccess={access["foundation-app"] == 200}
              columnAccess={access["column-app"] == 200}
              frameAccess={config.IS_DEV || access["frame-app"] == 200}
              sectionAccess={config.IS_DEV || access["section-app"] == 200}
              retainingWallAccess={config.IS_DEV || access["retainingwall-app"] == 200}
              basementWallAccess={config.IS_DEV || access["basementwall-app"] == 200}
              pileAccess={config.IS_DEV || access["pile-app"] == 200}
              cptAccess={config.IS_DEV || access["cpt-app"] == 200}
              className="mb-4"
              buttonTitle={t("add_project")}
              items={Object.values(user.projectdigest || {})}
              user={user}
              uploadProjectFile={(file: File) => {
                const projectGUid = user.projectdigest[selectedProjectName || ""]?.guid;
                uploadProjectFile(
                  file,
                  selectedProjectName ? selectedProjectName : null,
                  projectGUid ? projectGUid : null,
                  (name: string) => {
                    setProjectName(name);
                  },
                );
              }}
            />
          </Col>
        </Row>
        <Row className="flex-grow-1" style={{ minHeight: 0 }}>
          <Col className="h-100" style={{ minHeight: 0, padding: 0 }}>
            <ProjectPanelListToolbarView
              id="project-list"
              className="h-100"
              renderToolbarActions={() =>
                hasMultiProjectSelection ? (
                  <button
                    id="delete-projects-btn"
                    className="search-toggle-icon secondary-btn"
                    title={t("buttons.delete_multiple")}
                    onClick={() => setShowMultiProjectDeleteModal(true)}
                  >
                    <TrashIcon color="currentColor" size={16} />
                  </button>
                ) : selectedProjectName && user.projectdigest[selectedProjectName] ? (
                  <button
                    id="delete-project-btn"
                    className="search-toggle-icon secondary-btn"
                    title={allProjects.length <= 1 ? t("delete_modal.cannot_delete_last_project") : t("buttons.delete")}
                    disabled={allProjects.length <= 1}
                    onClick={() => {
                      setShowProjectDeleteModal(true);
                    }}
                  >
                    <TrashIcon color="currentColor" size={16} />
                  </button>
                ) : null
              }
              items={allProjects}
              getSortValue={(project: Project, mode: string) =>
                mode === "latestModified"
                  ? Math.max(...project.files.map((f) => parseUTC(f.lastModified).getTime()))
                  : project.name
              }
              getSearchTerms={(proj: Project) => [proj.name]}
              renderItem={(
                project: Project,
                _index: number,
                _realIndex: number,
                _nextProjectName: string,
                visibleItems: Project[],
              ) => (
                <ListGroup.Item
                  as="li"
                  key={project.id}
                  onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    setIsProjectDragOver(false);
                    setDragOverId(null);
                    if (e.dataTransfer.types.includes("application/x-item-id")) {
                      dropHandlerForProjects(e, project);
                    }
                  }}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes("application/x-item-id")) {
                      e.preventDefault();
                      setIsProjectDragOver(true);
                      setDragOverId(project.id);
                    }
                  }}
                  onDragEnter={(e) => {
                    if (e.dataTransfer.types.includes("application/x-item-id")) {
                      e.preventDefault();
                      setIsProjectDragOver(true);
                      setDragOverId(project.id);
                    }
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setIsProjectDragOver(false);
                      setDragOverId(null);
                    }
                  }}
                  className={`
                    ${selectedProjectName === project.guid || selectedProjectIds.includes(project.id) ? "selected-project" : ""}
                    ${dragOverId === project.id && isProjectDragOver ? "drag-over-project" : ""}
                  `}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    setSelectedFileIds([]);
                    setCheckedCount(0);

                    setSfileStatus(user?.projectdigest[project.guid]?.status ?? "active");
                    setSelectedProjectName(project.guid);
                    if (project.files.length > 0) {
                      setCopyOfCurrentSelectedFile({ ...project.files[0] });
                    }
                    setSelectedFile(0);

                    // Shift-click extends selection from the last anchor to the clicked row.
                    if (e.shiftKey) {
                      applyProjectRangeSelection(visibleItems, project);
                      return;
                    }

                    // Cmd on macOS / Ctrl on Windows/Linux toggles a single row in the current selection.
                    if (e.metaKey || e.ctrlKey) {
                      setSelectionAnchorId(project.id);
                      toggleProjectSelection(project.id);
                      return;
                    }

                    // Plain click focuses one project and resets any bulk selection state.
                    setSelectionAnchorId(project.id);
                    setSelectedProjectIds([]);
                  }}
                >
                  <Container fluid className="drag-container p-0">
                    <Row className="align-items-center p-2">
                      <Col xs={6}>
                        <span className="project-title mb-1">{project.name}</span>
                      </Col>
                      <Col style={{ textAlign: "right" }}>
                        {project.users && project.users.length > 0 && (
                          <span
                            className="badge"
                            style={{
                              border: "1px solid #ccc",
                              color: "#333",
                            }}
                          >
                            {t("members")}: {project.users.join(", ")}
                          </span>
                        )}
                      </Col>
                    </Row>
                    <Row className="align-items-center mt-2 p-2">
                      <Col xs={8}>
                        {project.beamAmount && (
                          <span className="badge bg-secondary me-2">Beams: {project.beamAmount}</span>
                        )}
                        {project.foundationAmount && (
                          <span className="badge bg-secondary me-2">Foundations: {project.foundationAmount}</span>
                        )}
                      </Col>
                      <Col style={{ textAlign: "right" }}>
                        {project.status && (
                          <span className={`badge ${project.status === "active" ? "bg-success" : "bg-secondary"}`}>
                            {t(`project.status.${project.status}`)}
                          </span>
                        )}
                      </Col>
                    </Row>
                  </Container>
                </ListGroup.Item>
              )}
            />
          </Col>
        </Row>
      </div>
    </Col>
  );
}
